import {Component, OnInit} from "@angular/core";
import {CirfService} from "../cirf.service";
import {ActivatedRoute, Router} from "@angular/router";
import {Cirf} from "../cirf";
import {FileUploadService} from "../../common/file-upload/file-upload.service";
import {CirfDocument} from "../cirf-document";
import {DpBooleanValueTypes, UploadedFileDescription} from "../../shared/constants";
import {CirfClientInfo} from "../cirf-client-info";
import {CirfMatterContactInfo} from "../cirf-matter-contact-info";
import {Address} from "../../shared/address";
import {CirfInsuranceDetail} from "../cirf-insurance-detail";
import {CirfMortgage} from "../cirf-mortgage";
import {Utils} from "../../common/utils";
import {dropDowns} from "../../shared/drop-downs";
import {Identification} from "../../shared/identification";
import {DatePipe} from "@angular/common";
import {CIRF_CONFIG_KEYS} from "../config/cirf-config-keys";
import {CommonService} from "../../common/common.service";

@Component({
    selector: 'dp-print-cirf',
    templateUrl: './print-cirf.component.html',
})
export class PrintCirfComponent implements OnInit {

    cirf : Cirf;
    cirfDocuments: CirfDocument[] = [];
    genderOptions : any = dropDowns.genderOptions;
    yesNoOptions : any = dropDowns.yesNoOptions;
    residentStatusOptions : any = dropDowns.residentStatusOptions;
    spousalStatusOptions: any = dropDowns.spousalStatusOptions;
    yesNoOptionsMandatory: any = dropDowns.yesNoOptionsMandatory;
    firstTimeHomeBuyerOptions: any = dropDowns.firstTimeHomeBuyerOptions;
    yesNoOnlyOptions : any = dropDowns.yesNoOnlyOptions;
    matterPurchasersCapacityOptions: any;
    frontText: string = 'Front';
    backText: string = 'Back';
    cleanNewMortgageNumber: number;
    cleanExistingMortgageNumber: number;

    constructor(public cirfService : CirfService,
                public router : Router,
                private route: ActivatedRoute,
                private datePipe : DatePipe,
                private commonService: CommonService,
                private fileUploadService: FileUploadService) {

    }

    ngOnInit() {
        this.cirf = this.cirfService.cirf;
        this.checkMortgageCleanup();
        let numberOfClients = this.cirfService.clients.length;
        if(this.cirf.isProvinceAB()){
            this.matterPurchasersCapacityOptions = dropDowns.matterPurchasersCapacityOptionsAB;
        } else {
            this.matterPurchasersCapacityOptions = Utils.getMatterPurchasersCapacityOptions(numberOfClients, this.cirf?.provinceCode);
        }

    }

    async checkMortgageCleanup(): Promise<void>{
        await this.getCirfDocuments();
        this.setCleanupFlagMortgage();
    }

    async getCirfDocuments(): Promise<void> {
        this.fileUploadService.getCirfDocuments(this.cirf.cirfGuid).subscribe(
            (documents)=>{
                this.cirfDocuments = documents;
                this.cirf?.cirfMatterData?.existingMortgage.forEach((mortgage : CirfMortgage)=>{
                    if(this.cirfDocuments.find(doc=>doc.sourceId == mortgage.id)){
                        mortgage.hasFileUpload = true;
                    }
                })

            })
    }

    getPermanentResidentCard(clientId: string) : CirfDocument {
        if(this.cirfDocuments && this.cirfDocuments.length){
            return this.cirfDocuments.find(doc => doc.sourceId == clientId && doc.description == UploadedFileDescription.permanentResidentCard);
        } else {
            return null;
        }
    }

    getDriverLicenseFront(clientId: string) : CirfDocument {
        if(this.cirfDocuments && this.cirfDocuments.length){
            return this.cirfDocuments.find(doc => doc.sourceId == clientId && doc.description == UploadedFileDescription.driverLicenseFront);
        } else {
            return null;
        }
    }

    getDriverLicenseBack(clientId: string) : CirfDocument {
        if(this.cirfDocuments && this.cirfDocuments.length){
            return this.cirfDocuments.find(doc => doc.sourceId == clientId && doc.description == UploadedFileDescription.driverLicenseBack);
        } else {
            return null;
        }
    }

    getUploadedFileFrontDescription(identificationTypeCode: string) : string {
        return Utils.getUploadedFileFrontDescription(identificationTypeCode);
    }

    getUploadedFileBackDescription(identificationTypeCode: string) : string {
        return Utils.getUploadedFileBackDescription(identificationTypeCode);
    }

    getUploadedDocument(identification: Identification, direction: string, clientId: string) : CirfDocument {
        let identificationTypeCode = identification?.identificationTypeCode;
        if(this.cirfDocuments && this.cirfDocuments.length && identificationTypeCode){
            let fileDescrition = direction == this.frontText ? this.getUploadedFileFrontDescription(identificationTypeCode) : this.getUploadedFileBackDescription(identificationTypeCode);
            return this.cirfDocuments.find(doc => doc.sourceId == clientId && doc.description == fileDescrition)
        } else {
            return null;
        }
    }

    getGeneralDocuments() : CirfDocument[]{
        return this.cirfDocuments.filter(doc => !doc.sourceId);
    }

    getMortgageDocuments(mortgageId: string) : CirfDocument[] {
    if(this.cirfDocuments && this.cirfDocuments.length){
        return this.cirfDocuments.filter(doc => doc.sourceId == mortgageId);
    } else {
        return null;
    }
}

    isNotResidingAtSubjectProperty(): boolean {
        return this.matterContactInfo?.residingAtSubjectProperty == DpBooleanValueTypes.NO;
    }

    isClosingAddressVisible() : boolean {
        return this.cirf?.isMatterMortgage()? this.isNotResidingAtSubjectProperty() : true;
    }

    get closingAddress(): string {
        if (this.cirf?.isMatterPurchase()) {
            return '(Pre-Closing Address) Address 1';
        }
        if(this.cirf?.isMatterMortgage()) {
            return '(Mailing Address) Address 1';
        }
        return '(Post-Closing Address) Address 1'
    }

    exitPreview() {
        this.router.navigate(['../cirf-home'], {relativeTo: this.route});
    }

    print() {
        window.print();
    }

    get cirfClients() : CirfClientInfo[] {
        return this.cirf?.cirfMatterData?.cirfClients
    }

    get matterContactInfo() : CirfMatterContactInfo {
        return this.cirf?.cirfMatterData?.matterContactInfo;
    }

    get preOrPostClosingAddress() : Address {
        return this.cirf?.isMatterPurchase() ? this.matterContactInfo.preClosingAddress : this.matterContactInfo.postClosingAddress
    }

    get fireInsuranceDetail() : CirfInsuranceDetail {
        return this.cirf?.cirfMatterData?.fireInsuranceDetail;
    }

    get mortgages(): CirfMortgage[] {
        return this.cirf?.cirfMatterData?.mortgages;
    }

    get newMortgages(): CirfMortgage[] {
        return this.cirf?.cirfMatterData?.newMortgages;
    }

    get existingMortgages(): CirfMortgage[] {
        return this.cirf?.cirfMatterData?.existingMortgage;
    }

    getLabelFromValue(value: string, dropdownArray : any[]) : string {
        return Utils.getLabelFromValue(value, dropdownArray);
    }

    isCustomMatter() : boolean {
        return this.cirf?.isCustomMatter();
    }

    get legalDescriptionSummary() : string {
        return this.cirf?.cirfMatterData?.legalDescriptionSummary.substring(0,100);
    }

    get closingDateText() :string {
        return this.cirf?.cirfMatterData?.closingDate ? `, ${this.isCustomMatter() ? 'completing' : 'closing'} on ` + this.datePipe.transform(this.cirf?.cirfMatterData?.closingDate, 'd/M/yyyy') : '' ;
    }

    get cirfClientInfoUdf() : string {
        return CIRF_CONFIG_KEYS.cirfClientInfoUdf;
    }

    get identification1Udf() : string {
        return CIRF_CONFIG_KEYS.identification1Udf;
    }

    get identification2Udf() : string {
        return CIRF_CONFIG_KEYS.identification2Udf;
    }

    get matterContactInfoUdf() : string {
        return CIRF_CONFIG_KEYS.matterContactInfoUdf;
    }

    get fireInsuranceDetailUdf() : string {
        return CIRF_CONFIG_KEYS.fireInsuranceDetailUdf;
    }

    get newMortgageUdf() : string {
        return CIRF_CONFIG_KEYS.newMortgageUdf;
    }

    get existingMortgageUdf() : string {
        return CIRF_CONFIG_KEYS.existingMortgageUdf;
    }

    formatExpiryDate(date: string) : string {
        return this.commonService.convertPartialDateToDisplay(date);
    }

    setCleanupFlagMortgageAction(mortgages: CirfMortgage[], isNewMortgage: boolean){
        if(isNewMortgage){
            this.cleanNewMortgageNumber = 0;
        } else {
            this.cleanExistingMortgageNumber = 0;
        }
        mortgages && mortgages.forEach(item=>{
            if(item.isCirfMortgageEmpty()){
                item.needCleanup = true;
                this.cirfService.cleanMortgageIds.push(item.id);
                if(isNewMortgage){
                    this.cleanNewMortgageNumber++;
                } else {
                    this.cleanExistingMortgageNumber++;
                }

            }
        });

    }

    setCleanupFlagMortgage(){
        this.cirfService.cleanMortgageIds = [];
        this.setCleanupFlagMortgageAction(this.cirf?.newMortgages, true);
        this.setCleanupFlagMortgageAction(this.cirf?.existingMortgages, false);
    }
}
